import { clientConfig } from '@/config/clientConfig';
import type { BulkActionResponse } from '@/domains/jobs/api/workOrdersApi';
import { BulkActionResultModal } from '@/domains/jobs/components/BulkActionResultModal';
import { BulkActionStepperModal } from '@/domains/jobs/components/BulkActionStepperModal';
import type {
  BulkActionMode,
  BulkActionStepperSubmitItem,
} from '@/domains/jobs/components/BulkActionStepperModal';
import { BulkActionsBar } from '@/domains/jobs/components/BulkActionsBar';
import { RequestMoreInfoButton } from '@/domains/jobs/components/RequestMoreInfoButton';
import { RequestMoreInfoOrReplyModal } from '@/domains/jobs/components/RequestMoreInfoOrReplyModal';
import type { BulkJobInput } from '@/domains/jobs/hooks/useWorkOrderActions';
import { PageHeader } from '@/shared/components/PageHeader';
import { Pagination } from '@/shared/components/Pagination';
import { useUrlFilters } from '@/shared/hooks/useUrlFilters';
import { cn } from '@/shared/lib/cn';
import { showSuccessToast } from '@/shared/notifications/toast-utils';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { AlertCircle, Eye, FileText, Loader2, Search as SearchIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { JOBS_PAGE_SIZE, JOB_LIST_DEFAULTS } from '../constants/jobListDefaults';
import { getStatusRowAccent } from '../constants/statusRowAccent';
import { getTabFilter } from '../constants/tabConfig';
import { useJobList } from '../hooks/useJobList';
import { useJobTabCounts } from '../hooks/useJobTabCounts';
import {
  useAcceptWorkOrders,
  useRejectWorkOrders,
  useReplyMoreInfoWorkOrders,
  useRequestMoreInfoWorkOrders,
} from '../hooks/useWorkOrderActions';
import { formatDuration } from '../lib/formatDuration';
import { formatRequiredBy } from '../lib/formatRequiredBy';
import type { WorkOrder } from '../types/workOrder';
import { JobListFilters } from './JobListFilters';
import { JobListTabs } from './JobListTabs';
import { PriorityBadge } from './PriorityBadge';
import { ResponseReceivedBadge } from './ResponseReceivedBadge';

interface JobListProps {
  onNavigateToDetail?: (orderId: string) => void;
}

export function JobList({ onNavigateToDetail }: JobListProps) {
  const [filters, setFilters] = useUrlFilters(JOB_LIST_DEFAULTS);

  const [selectedOrders, setSelectedOrders] = useState<Set<string>>(new Set());
  const [stepperMode, setStepperMode] = useState<BulkActionMode | null>(null);
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [requestMoreInfoModalOpen, setRequestMoreInfoModalOpen] = useState(false);
  const [resultModalState, setResultModalState] = useState<
    | {
        open: true;
        actionLabel: string;
        results: BulkActionResponse['results'];
        jobsLookup: { id: number; name: string | null }[];
      }
    | { open: false }
  >({ open: false });

  const { counts } = useJobTabCounts();

  const acceptWorkOrders = useAcceptWorkOrders();
  const rejectWorkOrders = useRejectWorkOrders();
  const requestMoreInfoWorkOrders = useRequestMoreInfoWorkOrders();
  const replyMoreInfoWorkOrders = useReplyMoreInfoWorkOrders();

  const activeTab = filters.tab;
  const currentPage = parseInt(filters.page, 10) || 1;
  const priorityFilter = filters.priority_id;
  const jobTypeFilter = filters.job_category_id;
  const searchTerm = filters.search_term;

  // A tab filters by EITHER work-order status names OR a MIR direction, never
  // both — "More Info Requested" has no status, so it filters on the outbound
  // pending MIR instead (see getTabFilter / tabConfig).
  const tabFilter = useMemo(() => getTabFilter(activeTab), [activeTab]);

  const {
    data: jobListData,
    isLoading,
    isError,
    refetch: refetchJobList,
  } = useJobList({
    limit: JOBS_PAGE_SIZE,
    skip: (currentPage - 1) * JOBS_PAGE_SIZE,
    priority_id:
      priorityFilter !== 'all'
        ? priorityFilter
            .split(',')
            .filter(Boolean)
            .map((id) => parseInt(id, 10))
        : undefined,
    job_category_id: jobTypeFilter !== 'all' ? parseInt(jobTypeFilter, 10) : undefined,
    search_term: searchTerm !== '' ? searchTerm : undefined,
    ...tabFilter,
  });

  const workOrders = useMemo((): WorkOrder[] => jobListData?.data ?? [], [jobListData]);
  const totalCount = jobListData?.total ?? 0;

  // Rows are rendered in the order the backend returns them (no client-side sort).
  const filteredOrders = workOrders;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedOrders(new Set(filteredOrders.map((o) => String(o.id))));
    } else {
      setSelectedOrders(new Set());
    }
  };

  const handleSelectOrder = (orderId: string, checked: boolean) => {
    const newSelected = new Set(selectedOrders);
    if (checked) {
      newSelected.add(orderId);
    } else {
      newSelected.delete(orderId);
    }
    setSelectedOrders(newSelected);
  };

  const handleAccept = () => setAcceptModalOpen(true);
  const handleReject = () => setStepperMode('reject');
  const handleRequestInfo = () => setRequestMoreInfoModalOpen(true);

  const allSelected =
    filteredOrders.length > 0 && filteredOrders.every((o) => selectedOrders.has(String(o.id)));

  const selectedJobs = useMemo(
    () => filteredOrders.filter((o) => selectedOrders.has(String(o.id))),
    [filteredOrders, selectedOrders],
  );

  const handleBulkResult = (response: BulkActionResponse, actionLabel: string) => {
    const results = response.results ?? [];
    const failed = results.filter((r) => !r.success);
    const lookup = selectedJobs.map((j) => ({ id: j.id, name: j.name }));

    if (failed.length === 0) {
      const n = results.length;
      showSuccessToast(`${n} job${n === 1 ? '' : 's'} ${actionLabel.toLowerCase()}`);
      setSelectedOrders(new Set());
      setStepperMode(null);
      setAcceptModalOpen(false);
      return;
    }

    setResultModalState({
      open: true,
      actionLabel,
      results,
      jobsLookup: lookup,
    });
    setSelectedOrders(new Set());
    setStepperMode(null);
    setAcceptModalOpen(false);
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <PageHeader
        title="Jobs"
        subtitle={`${counts.all ?? 0} total orders • Back Office Management`}
        icon={<FileText className="w-7 h-7 text-orange-600 dark:text-orange-400" />}
      />

      <div className="flex-1 overflow-y-auto">
        <div className="px-1">
          <JobListTabs />
        </div>

        <div className="px-1 pt-6 pb-4">
          <div className="flex items-center justify-between gap-4">
            <JobListFilters />

            <div className="flex items-center gap-2">
              {selectedJobs.length === 1 && (
                <RequestMoreInfoButton
                  mirPendingDirection={selectedJobs[0].mir_pending_direction}
                  onClick={handleRequestInfo}
                />
              )}
              {clientConfig.features.bulkActions && (
                <BulkActionsBar
                  selectedJobs={selectedJobs}
                  onAccept={handleAccept}
                  onReject={handleReject}
                />
              )}
            </div>
          </div>
        </div>

        <div className="px-1 pb-6">
          <div className="bg-card border-t-2 border-border overflow-hidden">
            <Table className="[&_th]:h-11 [&_td]:py-3">
              <TableHeader className="sticky top-0 bg-card/90 backdrop-blur-sm z-10">
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="w-12">
                    <Checkbox checked={allSelected} onCheckedChange={handleSelectAll} />
                  </TableHead>
                  <TableHead className="text-foreground font-semibold">Work Order ID</TableHead>
                  <TableHead className="text-foreground font-semibold">Job Type</TableHead>
                  <TableHead className="text-foreground font-semibold">Priority</TableHead>
                  <TableHead className="text-foreground font-semibold">Status</TableHead>
                  <TableHead className="text-foreground font-semibold">Location</TableHead>
                  <TableHead className="text-foreground font-semibold">Duration</TableHead>
                  <TableHead className="text-foreground font-semibold text-nowrap">
                    Required By
                  </TableHead>
                  <TableHead className="text-foreground font-semibold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.map((order) => {
                  const isSelected = selectedOrders.has(String(order.id));

                  return (
                    <TableRow
                      key={order.id}
                      className="border-border hover:bg-accent/50 cursor-pointer"
                      onClick={() => {
                        if (!isSelected && selectedOrders.size === 0) {
                          onNavigateToDetail?.(String(order.id));
                        }
                      }}
                    >
                      <TableCell
                        className={cn('border-l-4', getStatusRowAccent(order.work_order_status))}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) =>
                            handleSelectOrder(String(order.id), checked === true)
                          }
                        />
                      </TableCell>
                      <TableCell className="font-semibold">{order.name ?? '—'}</TableCell>
                      <TableCell className="text-foreground">
                        <div className="flex flex-col gap-1.5 items-start">
                          <span>{order.job_category_name ?? '—'}</span>
                          <ResponseReceivedBadge hasResponse={order.has_response} />
                        </div>
                      </TableCell>
                      <TableCell>
                        <PriorityBadge priority={order.priority_name} />
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {order.work_order_status ?? '—'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {order.address ?? '—'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDuration(order.duration, order.duration_unit)}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-nowrap">
                        {formatRequiredBy(order.req_completion_date)}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="size-8 p-0"
                          onClick={() => onNavigateToDetail?.(String(order.id))}
                        >
                          <Eye className="size-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {isLoading && (
              <div className="flex items-center justify-center h-[200px] text-muted-foreground">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin opacity-50" />
                  <p>Loading jobs...</p>
                </div>
              </div>
            )}
            {isError && !isLoading && (
              <div className="flex items-center justify-center h-[200px] text-muted-foreground">
                <div className="text-center">
                  <AlertCircle className="w-12 h-12 mx-auto mb-3 text-red-400 opacity-70" />
                  <p>Failed to load jobs</p>
                </div>
              </div>
            )}
            {!isLoading && !isError && filteredOrders.length === 0 && (
              <div className="flex items-center justify-center h-[200px] text-muted-foreground">
                <div className="text-center">
                  <SearchIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No jobs found</p>
                </div>
              </div>
            )}
          </div>

          {totalCount > 0 && (
            <Pagination
              page={currentPage}
              totalItems={totalCount}
              pageSize={JOBS_PAGE_SIZE}
              itemNoun="orders"
              onPageChange={(p) => setFilters({ page: String(p) })}
            />
          )}
        </div>

        <RequestMoreInfoOrReplyModal
          open={requestMoreInfoModalOpen}
          onOpenChange={setRequestMoreInfoModalOpen}
          job={selectedJobs.length === 1 ? selectedJobs[0] : null}
          isSubmitting={
            selectedJobs.length === 1 && selectedJobs[0].mir_pending_direction === 'inbound'
              ? replyMoreInfoWorkOrders.isPending
              : requestMoreInfoWorkOrders.isPending
          }
          onSubmit={(comment, files) => {
            if (selectedJobs.length !== 1) return;
            const filesMeta = files.map((f) => ({ name: f.name, size: f.size, type: f.type }));
            if (filesMeta.length > 0) {
              console.log('[request-more-info] attachments (not yet sent to backend):', filesMeta);
            }
            const payload = [
              {
                id: Number(selectedJobs[0].id),
                externalId: selectedJobs[0].external_id ?? '',
                comments: comment,
              },
            ];
            if (selectedJobs[0].mir_pending_direction === 'inbound') {
              replyMoreInfoWorkOrders.mutate(payload, {
                onSuccess: (resp) => {
                  setRequestMoreInfoModalOpen(false);
                  handleBulkResult(resp, 'Reply sent');
                  refetchJobList();
                },
                onError: (error) => {
                  setRequestMoreInfoModalOpen(false);
                  const message =
                    error instanceof Error ? error.message : 'An unexpected error occurred';
                  handleBulkResult(
                    { results: [{ id: selectedJobs[0].id, success: false, message }] },
                    'Reply sent',
                  );
                },
              });
            } else {
              requestMoreInfoWorkOrders.mutate(payload, {
                onSuccess: (resp) => {
                  setRequestMoreInfoModalOpen(false);
                  handleBulkResult(resp, 'Info request sent');
                  refetchJobList();
                },
                onError: (error) => {
                  setRequestMoreInfoModalOpen(false);
                  const message =
                    error instanceof Error ? error.message : 'An unexpected error occurred';
                  handleBulkResult(
                    { results: [{ id: selectedJobs[0].id, success: false, message }] },
                    'Info request sent',
                  );
                },
              });
            }
          }}
        />

        <BulkActionStepperModal
          open={stepperMode !== null}
          onOpenChange={(open) => {
            if (!open) setStepperMode(null);
          }}
          mode={stepperMode ?? 'reject'}
          jobs={selectedJobs.map((j) => ({
            id: j.id,
            name: j.name,
            address: j.address,
            work_order_status: j.work_order_status,
            req_completion_date: j.req_completion_date,
          }))}
          isSubmitting={rejectWorkOrders.isPending}
          onSubmit={(items: BulkActionStepperSubmitItem[]) => {
            const filesByJob = items
              .filter((it) => it.files.length > 0)
              .map((it) => ({
                id: it.id,
                files: it.files.map((f) => ({ name: f.name, size: f.size, type: f.type })),
              }));
            if (filesByJob.length > 0) {
              console.log('[bulk-action] attachments (not yet sent to backend):', filesByJob);
            }

            const payload: BulkJobInput[] = items.map(({ id, comments }) => {
              const job = selectedJobs.find((j) => j.id === id);
              return { id, externalId: job?.external_id ?? '', comments };
            });

            if (stepperMode === 'reject') {
              rejectWorkOrders.mutate(payload, {
                onSuccess: (resp) => {
                  handleBulkResult(resp, 'Rejected');
                  refetchJobList();
                },
              });
            }
          }}
        />

        <Dialog open={acceptModalOpen} onOpenChange={setAcceptModalOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>
                Accept {selectedOrders.size} job{selectedOrders.size === 1 ? '' : 's'}?
              </DialogTitle>
              <DialogDescription>
                This moves the selected jobs out of the "new" status.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setAcceptModalOpen(false)}
                disabled={acceptWorkOrders.isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className="text-white"
                style={{ backgroundColor: '#E84A2B' }}
                disabled={acceptWorkOrders.isPending}
                onClick={() => {
                  const ids = selectedJobs.map((j) => Number(j.id));
                  acceptWorkOrders.mutate(ids, {
                    onSuccess: (resp) => {
                      handleBulkResult(resp, 'Accepted');
                      refetchJobList();
                    },
                  });
                }}
              >
                {acceptWorkOrders.isPending ? 'Accepting…' : 'Accept'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {resultModalState.open && (
          <BulkActionResultModal
            open={resultModalState.open}
            onOpenChange={(open) => {
              if (!open) setResultModalState({ open: false });
            }}
            actionLabel={resultModalState.actionLabel}
            results={resultModalState.results}
            jobsLookup={resultModalState.jobsLookup}
          />
        )}
      </div>
    </div>
  );
}
